import type { MaterialData, RendererData } from '../../exports/behaviors';
import { applyMaterialSlotOverride } from './spatial-material-slot';

export type RendererOverrides = Partial<Omit<RendererData, '@type'>>;

export interface SpatialMaterialSpec {
    color?: string;
    /** A fraction, or a whole `{property}` binding the Renderer resolves. */
    metallic?: number | string;
    roughness?: number | string;
    emissive?: string;
    opacity?: number | string;
}

export const MATERIAL_SPEC_KEYS = ['color', 'metallic', 'roughness', 'emissive', 'opacity'] as const;

export const DEFAULT_MATERIAL_SLOT = 0;

const HEX6 = /^#[0-9A-Fa-f]{6}$/;
const HEX8 = /^#[0-9A-Fa-f]{8}$/;
/** A whole-field binding (`{glow}`, `{fade}`), which emissive, opacity,
 *  metallic and roughness keep verbatim for the Renderer to resolve — a state
 *  can then light a part up or fade it. `color` takes a hex only. Local rather than the behaviors' helper: this module ships in the
 *  pure build/spatial bundle. */
const WHOLE_BINDING = /^\{[^{}]+\}$/;

export function normalizeBaseColor(hex: string): string | null {
    const trimmed = hex.trim();
    if (HEX6.test(trimmed)) return trimmed.toUpperCase() + 'FF';
    if (HEX8.test(trimmed)) return trimmed.toUpperCase();
    return null;
}

export function normalizeEmissive(hex: string): string | null {
    const trimmed = hex.trim();
    if (HEX6.test(trimmed)) return trimmed.toUpperCase();
    if (HEX8.test(trimmed)) return trimmed.slice(0, 7).toUpperCase();
    return null;
}

export interface NormalizedMaterial {
    spec: SpatialMaterialSpec;
    warnings: string[];
}

export function normalizeMaterialSpec(input: unknown, objectName: string): NormalizedMaterial {
    if (input === null || typeof input !== 'object' || Array.isArray(input)) {
        throw new Error(
            `material("${objectName}", …): second argument must be an options object, `
            + `e.g. { color: "#C0C0C0", metallic: 1, roughness: 0.25 }.`,
        );
    }
    const bag = input as Record<string, unknown>;
    const warnings: string[] = [];
    const spec: SpatialMaterialSpec = {};

    const unknown = Object.keys(bag).filter(k => !(MATERIAL_SPEC_KEYS as readonly string[]).includes(k));
    if (unknown.length > 0) {
        throw new Error(
            `material("${objectName}"): unknown option(s) ${unknown.map(k => `"${k}"`).join(', ')}. `
            + `Allowed: ${MATERIAL_SPEC_KEYS.join(', ')}.`,
        );
    }

    const unit = (key: 'metallic' | 'roughness' | 'opacity'): void => {
        const raw = bag[key];
        if (raw === undefined) return;
        if (typeof raw === 'string' && WHOLE_BINDING.test(raw.trim())) {
            spec[key] = raw.trim();
            return;
        }
        if (typeof raw !== 'number' || !Number.isFinite(raw)) {
            throw new Error(`material("${objectName}"): ${key} must be a number between 0 and 1, or a {property} binding.`);
        }
        if (raw < 0 || raw > 1) {
            warnings.push(
                `material("${objectName}"): ${key} ${raw} is outside 0..1 and was clamped. `
                + `These are fractions, not percentages.`,
            );
        }
        spec[key] = Math.max(0, Math.min(1, raw));
    };
    unit('metallic');
    unit('roughness');
    unit('opacity');

    if (bag.color !== undefined) {
        if (typeof bag.color !== 'string') {
            throw new Error(`material("${objectName}"): color must be a hex string, e.g. "#8B7355".`);
        }
        const normalized = normalizeBaseColor(bag.color);
        if (normalized === null) {
            warnings.push(`material("${objectName}"): color "${bag.color}" is not #RRGGBB or #RRGGBBAA — ignored.${unboundColorNote(bag.color)}`);
        } else {
            spec.color = normalized;
        }
    }

    if (bag.emissive !== undefined) {
        if (typeof bag.emissive !== 'string') {
            throw new Error(
                `material("${objectName}"): emissive must be a hex COLOUR, e.g. "#FF3B30" — `
                + `it is the light the surface gives off, not a brightness number. `
                + `Use a darker hex for a dimmer glow.`,
            );
        }
        const emissive = bag.emissive.trim();
        const normalized = WHOLE_BINDING.test(emissive) ? emissive : normalizeEmissive(emissive);
        if (normalized === null) {
            warnings.push(`material("${objectName}"): emissive "${bag.emissive}" is not #RRGGBB or a {property} binding — ignored.`);
        } else {
            spec.emissive = normalized;
        }
    }

    return { spec, warnings };
}

/** For a colour written as a binding: a build's colour is a hex, never a binding, and what to do instead. */
export function unboundColorNote(value: unknown): string {
    return typeof value === 'string' && WHOLE_BINDING.test(value.trim())
        ? ' A build colour is a hex, not a binding: show one of two coloured copies by a scale binding instead.'
        : '';
}

export function mergeMaterialSpecs(
    a: SpatialMaterialSpec | undefined,
    b: SpatialMaterialSpec,
): SpatialMaterialSpec {
    return { ...a, ...b };
}

export function foldRendererOverrides(
    explicit: Record<string, unknown> | undefined,
    baseColor: string | undefined,
    material: SpatialMaterialSpec | undefined,
): RendererOverrides | undefined {
    const hasMaterial = material !== undefined && Object.keys(material).length > 0;
    if (!baseColor && !hasMaterial) {
        return explicit && Object.keys(explicit).length > 0 ? explicit : undefined;
    }

    // Model-authored bags arrive untyped; one widening at the boundary, the
    // same seam addBehavior() uses.
    const out = { ...explicit } as RendererOverrides;

    const slotPatch: Omit<MaterialData, 'targetMaterialIndex'> = {};
    // material({ color }) and color() are the same authored quantity; whichever
    // the descriptor ended up carrying is the one base colour.
    const color = material?.color ?? baseColor;
    if (color !== undefined) slotPatch.baseColorFactor = color;
    // A binding string rides in the numeric field, as authored data holds it.
    const factor = (value: number | string) => value as number;
    if (material?.metallic !== undefined) slotPatch.metallicFactor = factor(material.metallic);
    if (material?.roughness !== undefined) slotPatch.roughnessFactor = factor(material.roughness);
    if (material?.emissive !== undefined) slotPatch.emissiveFactor = material.emissive;

    if (Object.keys(slotPatch).length > 0) {
        const existing = Array.isArray(out.materials) ? out.materials : undefined;
        out.materials = applyMaterialSlotOverride(existing, DEFAULT_MATERIAL_SLOT, slotPatch);
    }
    if (material?.opacity !== undefined) out.opacity = factor(material.opacity);

    return out;
}

export function hasMaterialOverride(spec: SpatialMaterialSpec | undefined): boolean {
    if (!spec) return false;
    return spec.metallic !== undefined
        || spec.roughness !== undefined
        || spec.emissive !== undefined
        || spec.opacity !== undefined;
}
